import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useArealplan,
    usePlanbehandlinger,
    usePlandokumenterForPlan,
    useCreatePlanbehandling,
    useUpdatePlanbehandling,
    useDeletePlanbehandling,
    planbehandlingInputSchema,
} from '@origo/planregister';
import { Notice } from '@origo/origo-ui';
import PlanDetailSkeleton from '../../components/PlanDetailSkeleton';
import { useToast } from '@origo/origo-components';
import ProcessingsPage, { type NewProcessingState } from './ProcessingsPage';
import LinkedDocumentsSheet from '../documents/components/LinkedDocumentsSheet';
import type { ProcessingFieldErrors, ProcessingFormOptions } from './components/ProcessingForm';
import { useCodelistOptions } from '../../hooks/useCodelistOptions';
import {
    applyProcessingFormToPlanbehandling,
    emptyProcessingForm,
    toPlanbehandlingInput,
    toProcessingYearGroups,
} from '../../mappers/toProcessings';
import type { ProcessingFormValues, ProcessingItem } from '../../types';

interface ProcessingsContainerProps {
    napId: string;
}

/** Feltnære feil fra pakkens zod-schema — deles av opprett og rediger. */
function collectFieldErrors(input: unknown): ProcessingFieldErrors | null {
    const parsed = planbehandlingInputSchema.safeParse(input);
    if (parsed.success) return null;
    const next: ProcessingFieldErrors = {};
    for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof ProcessingFormValues | undefined;
        if (key && !next[key]) next[key] = issue.message;
    }
    return next;
}

const ProcessingsContainer: React.FC<ProcessingsContainerProps> = ({ napId }) => {
    const { t } = useTranslation();
    const toast = useToast();
    const { data, isLoading, isError } = usePlanbehandlinger(napId);
    const { data: plan } = useArealplan(napId);
    const { data: documents } = usePlandokumenterForPlan(napId);
    const codelists = useCodelistOptions();
    const create = useCreatePlanbehandling();
    const update = useUpdatePlanbehandling();
    const remove = useDeletePlanbehandling(napId);

    // Antall plandokumenter per behandling, vist på "Tilknyttede dokumenter"-knappen (#14686).
    const documentCounts = useMemo(() => {
        const counts = new Map<string, number>();
        for (const doc of documents ?? []) {
            if (!doc.planbehUid) continue;
            counts.set(doc.planbehUid, (counts.get(doc.planbehUid) ?? 0) + 1);
        }
        return counts;
    }, [documents]);

    const groups = useMemo(
        () => toProcessingYearGroups(data ?? [], codelists.behandlingDomains, documentCounts),
        [data, codelists.behandlingDomains, documentCounts]
    );

    const options: ProcessingFormOptions = useMemo(
        () => ({ planbehandlingtype: codelists.planbehandlingtype }),
        [codelists.planbehandlingtype]
    );

    // --- åpne/lukkede år-grupper ---
    const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
    // Auto-åpne nyeste daterte år ved første lasting — kun én gang per mount.
    const initializedRef = useRef(false);
    useEffect(() => {
        if (initializedRef.current || groups.length === 0) return;
        const newest = [...groups].reverse().find(g => g.year !== null) ?? groups[groups.length - 1];
        setOpenGroups(new Set([newest.key]));
        initializedRef.current = true;
    }, [groups]);

    const toggleGroup = useCallback((key: string) => {
        setOpenGroups(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    // --- opprett (#14408) ---
    const [newOpen, setNewOpen] = useState(false);
    const [newDraft, setNewDraft] = useState<ProcessingFormValues>(() => emptyProcessingForm());
    const [newErrors, setNewErrors] = useState<ProcessingFieldErrors>({});

    const openNew = useCallback(() => {
        setNewDraft(emptyProcessingForm({ saksnummer: plan?.saksnummer }));
        setNewErrors({});
        setNewOpen(true);
    }, [plan?.saksnummer]);

    const changeNew = useCallback((field: keyof ProcessingFormValues, value: string) => {
        setNewDraft(d => ({ ...d, [field]: value }));
        setNewErrors(e => (e[field] ? { ...e, [field]: undefined } : e));
    }, []);

    const saveNew = useCallback(async () => {
        const input = toPlanbehandlingInput(newDraft, napId, crypto.randomUUID());
        const errors = collectFieldErrors(input);
        if (errors) {
            setNewErrors(errors);
            return;
        }
        const created = await toast.execute(() => create.mutateAsync(input), {
            success: t('processings.created'),
        });
        if (created) {
            setNewOpen(false);
            // Sørg for at året den landet i er åpent.
            const year = created.dato ? String(created.dato.getUTCFullYear()) : 'undated';
            setOpenGroups(prev => new Set(prev).add(year));
        }
    }, [newDraft, napId, toast, create, t]);

    // --- rediger (#14409) ---
    const [fieldErrorsById, setFieldErrorsById] = useState<Record<string, ProcessingFieldErrors | undefined>>({});

    const saveItem = useCallback(
        (id: string, values: ProcessingFormValues) => {
            const original = data?.find(b => b.planbehUid === id);
            if (!original) return;
            const next = applyProcessingFormToPlanbehandling(original, values);
            const { objectId: _o, globalId: _g, ...input } = next;
            const errors = collectFieldErrors(input);
            setFieldErrorsById(prev => ({ ...prev, [id]: errors ?? undefined }));
            if (errors) return;
            toast.execute(() => update.mutateAsync(next), {
                success: t('common.saved'),
                error: t('common.errorSaving'),
            });
        },
        [data, toast, update, t]
    );

    // --- slett (#14409) ---
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const pendingDelete: ProcessingItem | null = useMemo(() => {
        if (!pendingDeleteId) return null;
        for (const g of groups) {
            const hit = g.items.find(i => i.id === pendingDeleteId);
            if (hit) return hit;
        }
        return null;
    }, [pendingDeleteId, groups]);

    const confirmDelete = useCallback(async () => {
        if (!pendingDelete) return;
        // mutateAsync gir void — pakk inn i `true` så vi kan skille suksess fra feil.
        const ok = await toast.execute(
            async () => {
                await remove.mutateAsync(pendingDelete.objectId);
                return true;
            },
            { success: t('processings.deleted') }
        );
        if (ok) setPendingDeleteId(null);
    }, [pendingDelete, toast, remove, t]);

    // --- tilknyttede dokumenter (#14686) ---
    const [documentsForPlanbehUid, setDocumentsForPlanbehUid] = useState<string | null>(null);

    if (isLoading || codelists.isLoading) return <PlanDetailSkeleton title={t('processings.title')} subtitle={t('processings.subtitle')} variant="list" />;
    if (isError) return <Notice variant="danger">{t('common.errorLoading')}</Notice>;

    const newProcessing: NewProcessingState = {
        open: newOpen,
        values: newDraft,
        fieldErrors: newErrors,
        isSaving: create.isPending,
    };

    return (
        <>
            <ProcessingsPage
                groups={groups}
                openGroups={openGroups}
                options={options}
                onGroupToggle={toggleGroup}
                newProcessing={newProcessing}
                onNewOpen={openNew}
                onNewClose={() => setNewOpen(false)}
                onNewChange={changeNew}
                onNewSave={saveNew}
                fieldErrorsById={fieldErrorsById}
                onItemSave={saveItem}
                onItemDeleteRequest={setPendingDeleteId}
                onShowDocuments={setDocumentsForPlanbehUid}
                pendingDelete={pendingDelete}
                onDeleteConfirm={confirmDelete}
                onDeleteCancel={() => setPendingDeleteId(null)}
                isSaving={update.isPending}
                isDeleting={remove.isPending}
            />
            <LinkedDocumentsSheet
                napId={napId}
                scope={documentsForPlanbehUid ? { kind: 'behandling', planbehUid: documentsForPlanbehUid } : null}
                onClose={() => setDocumentsForPlanbehUid(null)}
            />
        </>
    );
};

export default ProcessingsContainer;
